import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Brain, Share2, Sparkles, Menu, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../utils/supabase';

const Navbar = () => {
  const { session } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const controlNavbar = () => {
      setIsVisible(window.scrollY === 0);
      // Close menu when scrolling
      if (window.scrollY > 0) {
        setIsMenuOpen(false);
      }
    };

    controlNavbar();
    window.addEventListener('scroll', controlNavbar);
    
    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, []);

  const handleShare = async () => {
    try {
      const shareData = {
        title: 'The Most Useless Website',
        text: 'Check out this amazing collection of useless facts and achievements!',
        url: window.location.href
      };

      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
        
        const toast = document.createElement('div');
        toast.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
        toast.textContent = 'Link copied to clipboard!';
        document.body.appendChild(toast);
        
        setTimeout(() => {
          toast.remove();
        }, 3000);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      
      const toast = document.createElement('div');
      toast.className = 'fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
      toast.textContent = 'Failed to share. Please try again.';
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.remove();
      }, 3000);
    }
  };

  return (
    <header className={`bg-black/20 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-white/10 transition-transform duration-300 ${
      isVisible ? 'translate-y-0' : '-translate-y-full'
    }`}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 group hover-lift">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-300"></div>
              <div className="relative p-2 bg-black/40 rounded-xl group-hover:bg-black/50 transition-colors">
                <div className="relative">
                  <Brain className="h-7 w-7 text-transparent bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text" />
                  <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-yellow-400 animate-pulse" />
                </div>
              </div>
            </div>
            <div className="hidden md:block">
              <h1 className="text-2xl font-bold gradient-text">
                The Most Useless
              </h1>
            </div>
            <div className="block md:hidden">
              <h1 className="text-xl font-bold gradient-text">
                The Most Useless
              </h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <button 
              onClick={handleShare}
              className="flex items-center space-x-2 gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
            >
              <Share2 className="h-4 w-4" />
              <span>Share</span>
            </button>
            
            {session ? (
              <button
                onClick={() => supabase.auth.signOut()}
                className="text-gray-300 hover:text-white transition px-4 py-2 rounded-lg gradient-border"
              >
                Sign Out
              </button>
            ) : (
              <Link
                to="/login"
                className="gradient-text font-medium hover:opacity-80 transition px-4 py-2"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-white/5 hover:bg-white/10 transition"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen 
            ? 'max-h-48 opacity-100 mt-4' 
            : 'max-h-0 opacity-0 pointer-events-none'
        }`}>
          <div className="flex flex-col space-y-2 py-2">
            <button 
              onClick={() => {
                handleShare();
                setIsMenuOpen(false);
              }}
              className="flex items-center justify-center space-x-2 gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition w-full"
            >
              <Share2 className="h-4 w-4" />
              <span>Share</span>
            </button>
            
            {session ? (
              <button
                onClick={() => {
                  supabase.auth.signOut();
                  setIsMenuOpen(false);
                }}
                className="text-gray-300 hover:text-white transition px-4 py-2 rounded-lg gradient-border w-full"
              >
                Sign Out
              </button>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsMenuOpen(false)}
                className="gradient-text font-medium hover:opacity-80 transition px-4 py-2 text-center w-full"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;